import { useState } from 'react';

import { FloatingFocusManager, FloatingPortal } from '@floating-ui/react';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons';
import { faBug } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { AdminLinks } from '@spacefill/external-console/src/layout/AdminLinks';
import { CopyContextButton } from '@spacefill/external-console/src/layout/CopyContextButton';
import { BACKEND_LIST, BACKEND_URL_LIST } from '@spacefill/shared/src/constants/Backend';
import { FeatureTogglingToolbar } from '@spacefill/shared/src/utils/FeatureToggling';
import { getEnvironmentVariable } from '@spacefill/shared/src/utils/GetEnvVar';

import { Button } from './components/Button/index';
import { ButtonText } from './components/ButtonText';
import { SelectBackend } from './components/SelectBackend';
import { useFloatingDropdown } from './hooks/useFloatingDropdown';

export function SpacefillDebugActions() {
    const { isOpen, refs, floatingStyles, context, getReferenceProps, getFloatingProps, styles, isMounted, maxHeight } =
        useFloatingDropdown();

    const backends = Object.values(BACKEND_LIST);
    const [selectedBackend, setSelectedBackend] = useState(() => {
        const backendUrl = getEnvironmentVariable('backendUrl');
        const backendKey = Object.keys(BACKEND_URL_LIST).find(
            (key) => BACKEND_URL_LIST[key as keyof typeof BACKEND_URL_LIST] === backendUrl
        );
        return backendKey || 'LOCAL';
    });

    const envPrefix = getEnvironmentVariable('envPrefix');
    const externalConsoleServiceUrl = `${getEnvironmentVariable('externalConsoleServiceUrl')}/`;

    const isAdminConsole = envPrefix === 'ADMIN-CONSOLE';

    return (
        <div className='flex items-center gap-1.5'>
            <div
                ref={refs.setReference}
                {...getReferenceProps()}
            >
                <Button
                    variant='tertiary'
                    size='small'
                    iconStart={<FontAwesomeIcon icon={faBug} />}
                    dataTestid='debug'
                >
                    Debug
                </Button>
            </div>
            <>
                {isOpen && (
                    <FloatingPortal>
                        <FloatingFocusManager
                            context={context}
                            modal={false}
                        >
                            <div
                                ref={refs.setFloating}
                                style={{ ...floatingStyles, ...(maxHeight ? { maxHeight } : {}) }}
                                {...getFloatingProps()}
                                className='z-dropdown flex rounded-md bg-white p-2 drop-shadow-xl'
                            >
                                {isMounted && (
                                    <div
                                        style={styles}
                                        className='flex flex-col gap-3'
                                    >
                                        <SelectBackend
                                            backends={backends}
                                            selectedBackend={selectedBackend}
                                            setSelectedBackend={setSelectedBackend}
                                        />
                                        <FeatureTogglingToolbar />
                                        <CopyContextButton />
                                        {isAdminConsole ? (
                                            <div className='text-xs'>
                                                <ButtonText
                                                    href={externalConsoleServiceUrl}
                                                    type='external-link'
                                                    className='flex items-center w-full text-sm gap-2'
                                                >
                                                    Go to external console{' '}
                                                    <FontAwesomeIcon
                                                        icon={faArrowUpRightFromSquare}
                                                        className='text-xs'
                                                    />
                                                </ButtonText>
                                            </div>
                                        ) : (
                                            <AdminLinks />
                                        )}
                                    </div>
                                )}
                            </div>
                        </FloatingFocusManager>
                    </FloatingPortal>
                )}
            </>
        </div>
    );
}
